import React from 'react';
import { content, logoUrl } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { Facebook, Instagram, Linkedin, Twitter, MapPin, Phone, Mail, Plane } from 'lucide-react';

const Footer = () => {
  const { language } = useLanguage();
  const t = content[language]?.footer || content.en.footer;
  const nav = content[language]?.nav || content.en.nav;

  const scrollToSection = (href) => {
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="bg-slate-900 text-white">
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img src={logoUrl} alt="TransAfrica Canada" className="h-16 w-auto bg-white rounded-lg p-1" />
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              {t.description}
            </p>
            {/* Social links */}
            <div className="flex gap-3">
              {[
                { icon: Facebook, href: '#' },
                { icon: Instagram, href: '#' },
                { icon: Twitter, href: '#' },
                { icon: Linkedin, href: '#' }
              ].map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  className="w-10 h-10 rounded-lg bg-slate-800 hover:bg-blue-600 flex items-center justify-center transition-colors"
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6">{t.quickLinks}</h3>
            <ul className="space-y-3">
              {[
                { label: nav.home, href: '#home' },
                { label: nav.about, href: '#about' },
                { label: nav.services, href: '#services' },
                { label: nav.destinations, href: '#destinations' },
                { label: nav.pricing, href: '#pricing' },
                { label: nav.contact, href: '#contact' }
              ].map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-slate-400 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-6">{t.contactInfo}</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-400 text-sm">
                  3-425 McArthur Avenue<br />
                  Ottawa, Ontario, Canada
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-orange-500 flex-shrink-0" />
                <span className="text-slate-400 text-sm">+1 (613) 000-0000</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-orange-500 flex-shrink-0" />
                <span className="text-slate-400 text-sm">info@transafricacanada.com</span>
              </li>
            </ul>
          </div>

          {/* Destinations */}
          <div>
            <h3 className="text-lg font-bold mb-6">
              {language === 'fr' ? 'Nos Destinations' : 'Our Destinations'}
            </h3>
            <ul className="space-y-3">
              {[
                { name: 'Canada', flag: '🇨🇦' },
                { name: language === 'fr' ? 'Sénégal' : 'Senegal', flag: '🇸🇳' },
                { name: language === 'fr' ? 'Cameroun' : 'Cameroon', flag: '🇨🇲' },
                { name: 'RD Congo', flag: '🇨🇩' },
                { name: 'Congo', flag: '🇨🇬' }
              ].map((dest, index) => (
                <li key={index} className="flex items-center gap-2 text-slate-400 text-sm">
                  <span>{dest.flag}</span>
                  <span>{dest.name}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              {t.rights}
            </p>
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Plane className="w-4 h-4" />
              <span>{language === 'fr' ? 'Standards inspirés de DHL' : 'DHL-inspired standards'}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
